/* HILUNA immersive homepage prototype.
   No libraries: one rAF loop reads scroll position and sets transforms.
   Falls back to a calm static page with reduced motion, Save-Data or very weak devices. */
(() => {
  // each immersive section links this file; run it once
  if (window.__hlImmersive) return;
  window.__hlImmersive = true;
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  const weak = (navigator.hardwareConcurrency || 4) <= 2;
  if (reduced || saveData || weak) return; // static layout stays
  root.classList.add('im-on');

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const range = (t, a, b) => clamp((t - a) / (b - a));
  const ease = (x) => 1 - Math.pow(1 - x, 3);
  const easeIO = (x) => (x < .5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const lerp = (a, b, x) => a + (b - a) * x;
  const desktop = () => innerWidth > 900;
  const header = document.querySelector('[data-header]');

  /* ================= HERO ================= */
  const hero = document.querySelector('[data-ih]');
  const scenes = hero ? [...hero.querySelectorAll('[data-scene]')] : [];
  const N = scenes.length;
  // timeline in "units": one per product, then the dive into the material, then the veil.
  // Phones get a much shorter run so shoppers reach products quickly.
  let MAT = 1.1, VEIL = .7, TOTAL = N + MAT + VEIL;
  let heroTarget = 0, heroNow = 0;

  const els = scenes.map((s) => ({
    s, frame: s.querySelector('.ih__frame'), img: s.querySelector('img'), cap: s.querySelector('.ih__cap'),
    material: s.hasAttribute('data-material')
  }));
  const intro = hero && hero.querySelector('[data-ih-intro]');
  const veil = hero && hero.querySelector('[data-ih-veil]');
  const veilText = veil && veil.querySelector('p');
  const bar = hero && hero.querySelector('[data-ih-bar]');
  const count = hero && hero.querySelector('[data-ih-count]');

  const sizeHero = () => {
    if (!hero) return;
    const d = desktop();
    // computers: a short dive into the last product's texture. Phones: the opening simply ends on the last product.
    // The colour screen with words only appears if a text was set for it, and only on computers.
    const useVeil = d && veilText && veilText.textContent.trim() !== '';
    MAT = d ? .8 : 0; VEIL = useVeil ? .7 : 0; TOTAL = N + MAT + VEIL;
    veil.style.display = useVeil ? '' : 'none';
    const unit = d ? 65 : 34; // svh of scrolling per product
    hero.style.height = `calc(${TOTAL * unit}svh + 100svh)`;
    const m = els.find((e) => e.material);
    if (m) { veil.style.setProperty('--vx', m.img.style.getPropertyValue('--ox')); veil.style.setProperty('--vy', m.img.style.getPropertyValue('--oy')); }
  };

  const readHero = () => {
    const r = hero.getBoundingClientRect();
    const span = r.height - innerHeight;
    heroTarget = clamp(-r.top / span) * TOTAL;
  };

  let lastActive = -1;
  const drawHero = (t) => {
    const d = desktop();
    els.forEach((e, i) => {
      const enterStart = i - .4;               // next photo starts wiping in during the last 40% of the previous one
      const isLast = i === N - 1;
      const live = t >= enterStart && (isLast || t < i + 1.05);
      e.s.classList.toggle('is-live', live);
      if (!live) return;
      e.s.style.zIndex = i + 1;

      // wipe in from below (first one is already there)
      const enter = i === 0 ? 1 : easeIO(range(t, enterStart, i));
      e.frame.style.clipPath = `inset(${(1 - enter) * 100}% 0 0 0)`;

      // camera: slow push in + slight orbit while this product is on stage
      const k = range(t, enterStart, i + 1);
      let scale = lerp(1.14, 1.02, ease(clamp(k * 1.6))) + k * .08;
      let rot = lerp(3, -3, k);
      let ty = lerp(4, -3, k);
      if (e.material) {
        // dive into the concrete: zoom toward the texture until it fills the stage
        const dive = MAT > 0 ? easeIO(range(t, N - .15, N + MAT)) : 0;
        scale = lerp(scale, d ? 3.4 : 1.6, dive);
        rot *= 1 - dive; ty *= 1 - dive;
        if (d) {
          const w = Math.min(innerWidth * .54, innerHeight * .86);
          e.frame.style.width = lerp(w, innerWidth, dive) + 'px';
        }
        e.frame.style.setProperty('--shade', 1 - dive);
      }
      // the outgoing photo sinks a little while the next one covers it
      if (!isLast) ty -= range(t, i + .6, i + 1) * 6;
      e.img.style.transform = `perspective(1400px) translate3d(0, ${ty}%, 0) rotateY(${rot}deg) scale(${scale})`;

      // caption: rise in, hold, drift out
      const capIn = i === 0 ? range(t, .35, .6) : range(t, i - .05, i + .2);
      // the last caption stays on phones (no dive there), so the opening ends on a product with its price
      const capOut = isLast ? (MAT > 0 ? range(t, N - .3, N - .05) : 0) : range(t, i + .55, i + .8);
      const o = ease(capIn) * (1 - capOut);
      e.cap.style.opacity = o;
      e.cap.style.transform = `translate3d(0, ${(1 - ease(capIn)) * 28 - capOut * 28}px, 0)`;
      e.s.inert = o < .5;
    });

    // brand intro fades as the first product takes over
    const io = range(t, .02, .4);
    intro.style.opacity = 1 - io;
    intro.style.transform = `translate3d(0, ${-io * 60}px, 0)`;
    intro.style.visibility = io > .98 ? 'hidden' : ''; // the faded-out button must not catch taps

    // veil: paper colour opens out of the texture and hands off to the next section
    let v = 0;
    if (VEIL > 0) {
      v = easeIO(range(t, N + MAT - .25, TOTAL));
      veil.style.clipPath = `circle(${v * 150}% at var(--vx) var(--vy))`;
      const vt = range(t, N + MAT + .1, TOTAL);
      veilText.style.opacity = ease(vt);
      veilText.style.transform = `translate3d(0, ${(1 - ease(vt)) * 20}px, 0)`;
    }

    // counter
    const active = clamp(Math.round(t - .3), 0, N - 1);
    if (count && active !== lastActive) { count.textContent = String(active + 1).padStart(2, '0'); lastActive = active; }
    if (bar) bar.style.transform = `scaleX(${clamp(t / N)})`;

    // header: light on dark photos (phones), dark on the stone backdrop (desktop), normal once the veil is up
    // header: on computers it sits over the stone backdrop of the opening. On phones the opening starts
    // below the header, so the header keeps its normal dark logo and icons (a light logo there was invisible).
    if (header) {
      const over = heroInView && v < .6 && d;
      header.classList.toggle('im-over', over);
      header.classList.remove('im-dark');
    }
  };

  /* ================= HORIZONTAL GALLERY ================= */
  const hg = document.querySelector('[data-hg]');
  const track = hg && hg.querySelector('[data-hg-track]');
  const hgBar = hg && hg.querySelector('[data-hg-bar]');
  const hgImgs = track ? [...track.querySelectorAll('.hg__media img')] : [];
  let hgDist = 0, hgSpeed = 1, hgTarget = 0, hgNow = 0;

  const sizeGallery = () => {
    if (!hg) return;
    hgDist = Math.max(0, track.scrollWidth - innerWidth);
    // phones: the strip moves twice as fast, so the gallery takes about 2 screens instead of 3+
    hgSpeed = desktop() ? 1 : 2;
    hg.style.height = `${hgDist / hgSpeed + innerHeight}px`;
  };
  const readGallery = () => {
    const r = hg.getBoundingClientRect();
    hgTarget = clamp(-r.top / Math.max(1, r.height - innerHeight));
  };
  const drawGallery = (p) => {
    // RTL: the strip starts on the right, so it travels to the right (+x)
    track.style.transform = `translate3d(${p * hgDist}px, 0, 0)`;
    hgBar.style.transform = `scaleX(${p})`;
    hgImgs.forEach((img) => {
      const r = img.parentNode.getBoundingClientRect();
      const c = (r.left + r.width / 2) / innerWidth - .5; // -0.5 … 0.5 across the screen
      img.style.setProperty('--px', (c * -6).toFixed(2) + '%');
    });
  };

  /* ================= loop ================= */
  let heroInView = true, hgInView = false, running = false;
  const tick = () => {
    let busy = false;
    if (hero && heroInView) {
      readHero();
      heroNow += (heroTarget - heroNow) * .14; // gentle smoothing so it glides, still follows the finger exactly
      if (Math.abs(heroTarget - heroNow) < .0005) heroNow = heroTarget; else busy = true;
      drawHero(heroNow);
    } else if (header) header.classList.remove('im-over', 'im-dark');
    if (hg && hgInView) {
      readGallery();
      hgNow += (hgTarget - hgNow) * .14;
      if (Math.abs(hgTarget - hgNow) < .0005) hgNow = hgTarget; else busy = true;
      drawGallery(hgNow);
    }
    running = busy;
    if (busy) requestAnimationFrame(tick);
  };
  const kick = () => { if (!running) { running = true; requestAnimationFrame(tick); } };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.target === hero) heroInView = en.isIntersecting;
      if (en.target === hg) hgInView = en.isIntersecting;
    });
    kick();
  });
  hero && io.observe(hero);
  hg && io.observe(hg);

  /* ================= 3D HAMSA: load only when it gets close ================= */
  const hz = document.querySelector('[data-hz]');
  // phones: show one text at a time in the bottom slot, only while the block is on screen
  const hzPanels = hz ? [...hz.querySelectorAll('.hz__panel')] : [];
  const hzText = () => {
    if (!hz || !hzPanels.length) return;
    const r = hz.getBoundingClientRect();
    const stuck = innerWidth < 600 && r.top <= 1 && r.bottom >= innerHeight - 1;
    const p = clamp(-r.top / Math.max(1, r.height - innerHeight));
    const on = stuck ? Math.min(hzPanels.length - 1, Math.floor(p * hzPanels.length)) : -1;
    hzPanels.forEach((el, i) => {
      el.classList.toggle('is-on', i === on);
      el.firstElementChild.inert = innerWidth < 600 && i !== on;
    });
    requestAnimationFrame(() => hz.classList.add('hz-ready')); // fades only after the first placement
  };
  addEventListener('scroll', hzText, { passive: true });
  addEventListener('resize', hzText);
  if (hz) {
    const lazy = new IntersectionObserver(([en]) => {
      if (!en.isIntersecting) return;
      lazy.disconnect();
      import(hz.dataset.module).then((m) => m.init(hz)).catch(() => {}); // photo stays on failure
    }, { rootMargin: '150% 0px' });
    lazy.observe(hz);
  }

  const resize = () => { sizeHero(); sizeGallery(); hzText(); kick(); };
  addEventListener('scroll', kick, { passive: true });
  addEventListener('resize', resize);
  addEventListener('load', resize);
  // keyboard users: if focus lands in the gallery, bring it on screen
  track && track.addEventListener('focusin', (e) => {
    const items = [...track.children];
    const i = items.indexOf(e.target.closest('li'));
    if (i < 0) return;
    const top = hg.offsetTop + (i / (items.length - 1)) * (hgDist / hgSpeed);
    scrollTo({ top, behavior: 'auto' });
  });
  resize();
})();
